// frontend/src/utils/clientValidation.js

/**
 * Client validation utilities for task assignment and other business rules
 */

// Client statuses that allow task assignment
const ACTIVE_CLIENT_STATUSES = ['active'];

// Client statuses that do not allow task assignment
const INACTIVE_CLIENT_STATUSES = ['prospect', 'paused', 'churned', 'closed_lost', 'inactive', 'archived'];

/**
 * Check if a client can have tasks assigned to them
 * @param {Object} client - The client object
 * @param {string} client.status - The client's status
 * @returns {boolean} - True if tasks can be assigned, false otherwise
 */
export const canAssignTasksToClient = (client) => {
    if (!client || !client.status) {
        return false;
    }
    
    return ACTIVE_CLIENT_STATUSES.includes(client.status.toLowerCase());
};

/**
 * Get a list of clients that can have tasks assigned
 * @param {Array} clients - Array of client objects
 * @returns {Array} - Filtered array of clients that can have tasks assigned
 */
export const getActiveClientsForTaskAssignment = (clients) => {
    if (!Array.isArray(clients)) {
        return [];
    }
    
    return clients.filter(canAssignTasksToClient);
};

/**
 * Get client status validation message
 * @param {string} status - The client status
 * @returns {string} - Validation message explaining why tasks cannot be assigned
 */
export const getClientStatusValidationMessage = (status) => {
    const statusMessages = {
        'prospect': 'Tasks cannot be assigned to prospects. Please convert the client to active status first.',
        'paused': 'Tasks cannot be assigned to paused clients. Please reactivate the client first.',
        'churned': 'Tasks cannot be assigned to churned clients.',
        'closed_lost': 'Tasks cannot be assigned to closed lost clients.',
        'inactive': 'Tasks cannot be assigned to inactive clients. Please activate the client first.',
        'archived': 'Tasks cannot be assigned to archived clients.'
    };
    
    return statusMessages[status?.toLowerCase()] || 'Tasks cannot be assigned to this client due to their current status.';
};

/**
 * Validate client for task assignment with detailed error info
 * @param {Object} client - The client object
 * @returns {Object} - Validation result with isValid boolean and message
 */
export const validateClientForTaskAssignment = (client) => {
    if (!client) {
        return {
            isValid: false,
            message: 'Client information is required.'
        };
    }
    
    if (!client.status) {
        return {
            isValid: false,
            message: 'Client status is required.'
        };
    }
    
    const canAssign = canAssignTasksToClient(client);
    
    return {
        isValid: canAssign,
        message: canAssign ? 'Client can have tasks assigned.' : getClientStatusValidationMessage(client.status)
    };
};

/**
 * Client status configuration for UI display
 */
export const CLIENT_STATUS_CONFIG = {
    active: {
        label: 'Active',
        canAssignTasks: true,
        color: 'green',
        description: 'Client is actively engaged and can have tasks assigned'
    },
    prospect: {
        label: 'Prospect',
        canAssignTasks: false,
        color: 'blue',
        description: 'Potential client - tasks cannot be assigned until converted to active'
    },
    paused: {
        label: 'Paused',
        canAssignTasks: false,
        color: 'orange',
        description: 'Client relationship is paused - tasks cannot be assigned'
    },
    churned: {
        label: 'Churned',
        canAssignTasks: false,
        color: 'red',
        description: 'Client has churned - tasks cannot be assigned'
    },
    closed_lost: {
        label: 'Closed Lost',
        canAssignTasks: false,
        color: 'gray',
        description: 'Opportunity was lost - tasks cannot be assigned'
    },
    inactive: {
        label: 'Inactive',
        canAssignTasks: false,
        color: 'gray',
        description: 'Client is inactive - tasks cannot be assigned'
    },
    archived: {
        label: 'Archived',
        canAssignTasks: false,
        color: 'gray',
        description: 'Client is archived - tasks cannot be assigned'
    }
};

export default {
    canAssignTasksToClient,
    getActiveClientsForTaskAssignment,
    getClientStatusValidationMessage,
    validateClientForTaskAssignment,
    CLIENT_STATUS_CONFIG
};
